import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3,
  Building2,
  ExternalLink,
  QrCode,
  Download,
  Users,
  Sparkles,
  BookHeart,
  Bookmark,
  Gift,
  Activity,
  MessageSquare,
} from "lucide-react";
import { sponsorApi } from "../../lib/api";

const RANGE_OPTIONS = [
  ["today", "Today"],
  ["7d", "7 days"],
  ["30d", "30 days"],
  ["90d", "90 days"],
  ["", "All time"],
];

const TOPIC_LABELS = {
  anxiety: "Anxiety",
  relationships: "Relationships",
  marriage: "Marriage",
  family: "Family",
  finances: "Finances",
  grief: "Grief",
  loneliness: "Loneliness",
  work_career: "Work / Career",
  faith: "Faith",
  purpose: "Purpose",
  stress: "Stress",
  parenting: "Parenting",
  other: "Other",
};

export default function SponsorDashboardPage() {
  const { sponsor_id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [range, setRange] = useState("30d");

  useEffect(() => {
    setData(null);
    sponsorApi
      .dashboard(sponsor_id, range || undefined)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load"));
  }, [sponsor_id, range]);

  if (err) {
    return (
      <div
        data-testid="sponsor-dashboard-error"
        className="rounded-2xl border border-[#D9BEB5] bg-[#F7ECE8] text-[#7C3E33] p-6"
      >
        {err}
      </div>
    );
  }
  if (!data) return <div className="text-[#6B6B6B]">Loading…</div>;

  const s = data.sponsor;
  const a = data.aggregates;
  const publicUrl = `${window.location.origin}/sponsor/${s.sponsor_slug}`;
  const primary = s.primary_color || "#4F6F52";

  const qrUrl = `${process.env.REACT_APP_BACKEND_URL}/api/admin/sponsors/${s.sponsor_id}/qr.png`;
  const token = localStorage.getItem("lg_token");

  const copyLink = async () => {
    await navigator.clipboard.writeText(publicUrl);
    toast.success("Link copied");
  };
  const downloadQr = async () => {
    try {
      const r = await fetch(qrUrl, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a2 = document.createElement("a");
      a2.href = url;
      a2.download = `${s.sponsor_slug}-qr.png`;
      a2.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to download QR code");
    }
  };

  return (
    <div data-testid="sponsor-dashboard-page" className="space-y-8 fade-in-up">
      {/* -- Header ------------------------------------------------------ */}
      <div className="rounded-3xl border border-[#DCE5DB] bg-white p-6 flex flex-col sm:flex-row gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={16} style={{ color: primary }} />
            <span
              className="text-xs uppercase tracking-[0.18em] font-semibold"
              style={{ color: primary }}
            >
              {s.sponsor_type.replace(/_/g, " ")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {s.logo_url && (
              <img
                src={s.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${s.logo_url}` : s.logo_url}
                alt=""
                className="h-10 w-auto rounded-md"
                data-testid="sponsor-header-logo"
              />
            )}
            <h1 className="font-display text-3xl">{s.sponsor_name}</h1>
          </div>
          <p className="text-sm text-[#6B6B6B] mt-2">
            Audience: <strong>{s.audience_size ?? "—"}</strong> · Aggregate
            analytics only — no individual user activity is shared.
          </p>
          <div className="mt-4 space-y-1.5 text-sm">
            <div>
              <span className="text-[#6B6B6B]">Landing URL: </span>
              <code
                data-testid="sponsor-public-url"
                className="text-[#4F6F52] break-all"
              >
                {publicUrl}
              </code>{" "}
              <button
                data-testid="sponsor-copy-url"
                onClick={copyLink}
                className="text-xs underline underline-offset-2 text-[#6B6B6B] hover:text-[#4F6F52]"
              >
                Copy
              </button>{" "}
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener"
                className="inline-flex items-center text-[#6B6B6B] hover:text-[#4F6F52]"
              >
                <ExternalLink size={12} />
              </a>
            </div>
            <div>
              <span className="text-[#6B6B6B]">Referral code: </span>
              <code className="text-[#4F6F52]">{s.referral_code}</code>
            </div>
          </div>
        </div>
        <div className="text-center">
          <div className="flex items-center gap-2 text-[#4F6F52] mb-2 justify-center">
            <QrCode size={13} />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">
              QR code
            </span>
          </div>
          <img
            data-testid="sponsor-qr-img"
            alt="Sponsor QR"
            src={qrUrl + `?t=${token || ""}`}
            /* Use fetch-based download instead of anchor so auth header is sent */
            onError={() => {}}
            className="w-32 h-32 rounded-xl bg-white border border-[#DCE5DB] p-1"
            crossOrigin="anonymous"
          />
          <button
            data-testid="sponsor-download-qr"
            onClick={downloadQr}
            className="mt-2 inline-flex items-center gap-1 text-xs text-[#4F6F52] hover:text-[#3F5A42]"
          >
            <Download size={12} /> Download
          </button>
        </div>
      </div>

      {/* -- Range filter ----------------------------------------------- */}
      <div className="flex items-center justify-between">
        <label className="text-sm text-[#6B6B6B]">
          Range:{" "}
          <select
            data-testid="sponsor-range-filter"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="bg-white border border-[#DCE5DB] rounded-2xl px-3 py-1.5 ml-1"
          >
            {RANGE_OPTIONS.map(([v, l]) => (
              <option key={v || "all"} value={v}>
                {l}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* -- KPI cards --------------------------------------------------- */}
      <section>
        <h2 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#4F6F52] mb-3 flex items-center gap-2">
          <BarChart3 size={13} /> Attribution funnel
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Landing visits" value={a.page_visits} testid="sponsor-stat-page_visits" />
          <Kpi label="Referral clicks" value={a.referral_clicks} testid="sponsor-stat-referral_clicks" />
          <Kpi label="Signups" value={a.attributed_signups} testid="sponsor-stat-attributed_signups" />
          <Kpi label="Active users" value={a.active_users} testid="sponsor-stat-active_users" />
          <Kpi label="Pro trials" value={a.trials} testid="sponsor-stat-trials" />
          <Kpi label="Paid Pro" value={a.attributed_paid} testid="sponsor-stat-attributed_paid" />
          <Kpi label="Gifts purchased" value={a.gifts} testid="sponsor-stat-gifts" />
          <Kpi
            label="Conversion rate"
            value={`${a.conversion_rate}%`}
            testid="sponsor-stat-conversion"
          />
          <Kpi
            label="Total reach rate"
            value={a.total_reach_rate == null ? "Not available" : `${a.total_reach_rate}%`}
            testid="sponsor-stat-total_reach"
          />
          <Kpi
            label="Active adoption rate"
            value={a.active_adoption_rate == null ? "Not available" : `${a.active_adoption_rate}%`}
            testid="sponsor-stat-active_adoption"
          />
        </div>
      </section>

      {/* -- Downloads --------------------------------------------------- */}
      <section className="flex flex-wrap gap-2 text-sm">
        <a
          data-testid="sponsor-download-pdf"
          href={`${process.env.REACT_APP_BACKEND_URL}/api/sponsor/${sponsor_id}/report.pdf${range ? "?range=" + range : ""}`}
          onClick={async (e) => {
            e.preventDefault();
            const r = await fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${token}` } });
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a2 = document.createElement("a");
            a2.href = url;
            a2.download = `${s.sponsor_slug}-impact-report.pdf`;
            a2.click();
            URL.revokeObjectURL(url);
          }}
          className="inline-flex items-center gap-1.5 bg-white border border-[#DCE5DB] hover:border-[#4F6F52]/40 rounded-full px-4 py-2 text-[#4F6F52]"
        >
          <Download size={13} /> Impact Report (PDF)
        </a>
        <a
          data-testid="sponsor-download-csv"
          href="#"
          onClick={async (e) => {
            e.preventDefault();
            const r = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/sponsor/${sponsor_id}/export.csv${range ? "?range=" + range : ""}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a2 = document.createElement("a");
            a2.href = url;
            a2.download = `${s.sponsor_slug}-analytics.csv`;
            a2.click();
            URL.revokeObjectURL(url);
          }}
          className="inline-flex items-center gap-1.5 bg-white border border-[#DCE5DB] hover:border-[#4F6F52]/40 rounded-full px-4 py-2 text-[#4F6F52]"
        >
          <Download size={13} /> Export CSV
        </a>
      </section>

      {/* -- Trend charts ------------------------------------------------ */}
      <section>
        <h2 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#4F6F52] mb-3">
          Trends
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <MiniChart title="New signups" data={a.timeseries?.new_signups} color={primary} testid="chart-signups" />
          <MiniChart title="Landing visits" data={a.timeseries?.page_visits} color={primary} testid="chart-visits" />
          <MiniChart title="Referral clicks" data={a.timeseries?.referral_clicks} color={primary} testid="chart-clicks" />
          <MiniChart title="Paid conversions" data={a.timeseries?.attributed_paid} color={primary} testid="chart-paid" />
          <MiniChart title="Gifts purchased" data={a.timeseries?.gifts} color={primary} testid="chart-gifts" />
        </div>
      </section>

      {/* -- Impact section --------------------------------------------- */}
      <section>
        <h2 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#4F6F52] mb-3">
          Impact
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Impact icon={Users} label="People reached" value={a.impact.people_reached} />
          <Impact icon={Sparkles} label="People using LifeGuide" value={a.impact.people_using_lifeguide} />
          <Impact icon={MessageSquare} label="Guidance sessions" value={a.impact.guidance_sessions} />
          <Impact icon={BookHeart} label="Journal engagement" value={a.impact.journal_engagement} />
          <Impact icon={Bookmark} label="Saved Wisdom engagement" value={a.impact.saved_wisdom_engagement} />
          <Impact icon={Gift} label="Gifts shared" value={a.impact.gifts_shared} />
        </div>
      </section>

      {/* -- Anonymous topic insights ----------------------------------- */}
      <section>
        <h2 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#4F6F52] mb-3">
          Anonymous topic trends
        </h2>
        <div className="rounded-3xl border border-[#DCE5DB] bg-white p-5">
          <p className="text-xs text-[#6B6B6B] mb-3">
            Aggregate categories only. Any category with fewer than{" "}
            <strong>{a.topics?.privacy_threshold ?? 5}</strong> attributed users
            in this window is suppressed to protect individual privacy.
          </p>
          <TopicList
            data-testid="sponsor-topic-list"
            categories={a.topics?.categories || {}}
            suppressed={a.topics?.suppressed_total || 0}
          />
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#DCE5DB] bg-white p-4"
    >
      <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B6B6B]">{label}</div>
      <div className="text-2xl font-display mt-1">{value ?? "—"}</div>
    </div>
  );
}

function Impact({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-[#DCE5DB] bg-white p-4">
      <div className="flex items-center gap-2 text-[#4F6F52] mb-1.5">
        <Icon size={13} />
        <span className="text-[10px] uppercase tracking-[0.14em] font-semibold">
          {label}
        </span>
      </div>
      <div className="text-2xl font-display">{value ?? 0}</div>
    </div>
  );
}

function TinyBarChart({ data, testid, color }) {
  if (!data || data.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#DCE5DB] p-8 text-center text-sm text-[#6B6B6B]" data-testid={testid}>
        No data yet.
      </div>
    );
  }
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-[#DCE5DB] bg-white p-5"
    >
      <div className="flex items-end gap-1.5 h-32">
        {data.map((d) => (
          <div
            key={d.day}
            className="flex-1 rounded-t-md"
            style={{
              height: `${(d.count / max) * 100}%`,
              backgroundColor: color,
              opacity: 0.85,
            }}
            title={`${d.day}: ${d.count}`}
          />
        ))}
      </div>
      <div className="flex justify-between mt-2 text-[10px] text-[#6B6B6B]">
        <span>{data[0]?.day || ""}</span>
        <span>{data[data.length - 1]?.day || ""}</span>
      </div>
    </div>
  );
}

function MiniChart({ title, data, color, testid }) {
  return (
    <div className="rounded-2xl border border-[#DCE5DB] bg-white p-4">
      <div className="text-[11px] uppercase tracking-[0.14em] text-[#6B6B6B] mb-2">{title}</div>
      <TinyBarChart data={data || []} color={color} testid={testid} />
    </div>
  );
}

function TopicList({ categories, suppressed }) {
  const entries = Object.entries(categories);
  const total = entries.reduce((n, [, v]) => n + v, 0) + suppressed;
  if (total === 0) {
    return (
      <div className="text-sm text-[#6B6B6B] italic" data-testid="sponsor-topic-empty">
        Not enough data yet. Topic trends will appear once attributed users
        engage with LifeGuide guidance.
      </div>
    );
  }
  if (entries.length === 0) {
    return (
      <div className="text-sm text-[#6B6B6B]" data-testid="sponsor-topic-suppressed-only">
        All topic counts are below the privacy threshold. Combined total:{" "}
        <strong>{suppressed}</strong>.
      </div>
    );
  }
  const sorted = [...entries].sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-2">
      {sorted.map(([cat, count]) => {
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={cat} data-testid={`sponsor-topic-${cat}`}>
            <div className="flex justify-between text-sm mb-1">
              <span>{TOPIC_LABELS[cat] || cat}</span>
              <span className="text-[#6B6B6B]">
                {count} · {pct}%
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-[#EEF3EC] overflow-hidden">
              <div
                className="h-full bg-[#4F6F52]"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
      {suppressed > 0 && (
        <div className="text-xs text-[#6B6B6B] italic mt-2">
          + {suppressed} additional attributed activities in small categories
          (suppressed for privacy).
        </div>
      )}
    </div>
  );
}
